import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  User,
  Clock,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  MessageCircle,
  ArrowLeft,
  ChevronRight,
  Heart,
  Bookmark,
  Eye,
  ThumbsUp,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  X,
  Star
} from 'lucide-react';

const BlogPost = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [commentData, setCommentData] = useState({
    name: '',
    email: '',
    comment: ''
  });
  const [commentStatus, setCommentStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // All blog posts data with the new posts
  const postsData = {
    "mattress-stain-removal-what-works": {
      title: "Mattress Stain Removal: What Actually Works",
      slug: "mattress-stain-removal-what-works",
      excerpt: "Remember that time you knocked over your coffee mug at 6 AM? Or when your toddler had that accident? I've been there. Multiple times. Everyone deals with mattress stains. Here's what actually works.",
      author: "Sarah Mitchell",
      authorTitle: "Senior Cleaning Specialist",
      authorBio: "With over 10 years of experience in professional cleaning, Sarah has tackled every type of stain imaginable. She's passionate about helping people maintain healthier homes.",
      authorImage: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=2070&auto=format&fit=crop",
      date: "February 24, 2025",
      readTime: "8 min read",
      views: 1247,
      likes: 89,
      comments: 23,
      category: "Mattress Cleaning",
      tags: ["mattress cleaning", "stain removal", "DIY cleaning", "bedroom maintenance", "cleaning tips"],
      image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=2070&auto=format&fit=crop",
      imageCaption: "Professional mattress cleaning can extend the life of your mattress by years",
      
      // Full content with HTML formatting
      content: `
        <p class="lead">Remember that time you knocked over your coffee mug at 6 AM? Or when your toddler had that accident? I've been there. Multiple times.</p>
        
        <p>Everyone deals with mattress stains. The internet is full of solutions that sound great but do nothing. I'm going to share what actually works because I've tried everything.</p>
        
        <div class="bg-blue-50 border-l-4 border-blue-600 p-4 my-6 rounded-r-lg">
          <p class="font-semibold text-blue-800">💡 Quick Tip:</p>
          <p class="text-blue-700">Big stains covering half your mattress? Just call professional mattress cleaning services. Save yourself the trouble. But small everyday messes? You can fix those right now.</p>
        </div>
        
        <h2>Why Mattresses Soak Up Everything</h2>
        
        <p>Your mattress is basically a giant sponge covered in fabric. That's the whole problem.</p>
        
        <p>Spill water on it. Watch how fast it disappears. Through the cover. Into the foam. Down to the springs. Every layer gets soaked.</p>
        
        <p>Can't toss it in the washer either. You're dealing with something huge and heavy. Takes forever to dry. This is why speed matters.</p>
        
        <p>Your body heat keeps the mattress warm. Add moisture from stains. Perfect recipe for dust mites and bacteria. Now your cosmetic problem is a health problem.</p>
        
        <h2>The Types of Stains You'll Face</h2>
        
        <h3>Pee Stains</h3>
        <p>Kids. Pets. Sometimes adults. Happens to everyone. Urine spreads fast and smells worse. Wait too long and you're screwed.</p>
        
        <h3>Blood</h3>
        <p>Nosebleeds happen. Periods happen. Cuts happen. Blood is protein-based. Use hot water and you've made it permanent. Cold water only.</p>
        
        <h3>Sweat Marks</h3>
        <p>Those yellow patches from your body oils. They sneak up on you. One day your mattress just looks gross.</p>
        
        <h3>Food and Drinks</h3>
        <p>Wine. Coffee. Juice. Midnight snacks. Immediate stains that vary in difficulty.</p>
        
        <div class="bg-gray-100 p-6 rounded-xl my-8">
          <h3 class="text-xl font-bold mb-4">🛠️ What Actually Removes Stains</h3>
          <p class="mb-4">Skip the weird Pinterest recipes. Most don't work anyway.</p>
          
          <h4 class="font-bold mb-2">What You Need:</h4>
          <ul class="grid grid-cols-2 gap-2 list-disc pl-6">
            <li>Baking Soda</li>
            <li>White Vinegar</li>
            <li>Hydrogen Peroxide (3%)</li>
            <li>Dish Soap</li>
            <li>Spray Bottles</li>
            <li>White Towels</li>
            <li>Vacuum with Brush Attachment</li>
          </ul>
          <p class="mt-4 text-gray-700 italic">That's it. No fancy expensive products.</p>
        </div>
        
        <h2>How To Clean Different Stains</h2>
        
        <h3>Fresh Pee</h3>
        <p>Found it? Start now.</p>
        <ol class="list-decimal pl-6 space-y-2 my-4">
          <li>Strip everything. Grab your towels. Press hard into the wet spot. Don't rub. Just press and absorb.</li>
          <li>Mix vinegar and water. Half and half. Spray the entire stain. Vinegar kills bacteria. Neutralizes that ammonia stink. Wait 15 minutes.</li>
          <li>Blot again with dry towels. Cover the whole area with baking soda. Use a lot. Let it sit overnight. Longer if you can. Vacuum it all up.</li>
        </ol>
        
        <p>Old pee stain? Mix hydrogen peroxide (one cup), baking soda (three tablespoons), and a drop of dish soap. Spread this paste on the stain. Let it dry. It'll look weird and crusty. Vacuum. Still there after two tries? Call professional cleaning experts. Stop wasting your time.</p>
        
        <h3>Blood Stains</h3>
        <p>Cold water. That's the rule. Hot water sets it forever.</p>
        <ol class="list-decimal pl-6 space-y-2 my-4">
          <li>Wet a cloth with cold water. Dab the blood. Blot from outside to center. Stops it from spreading.</li>
          <li>Not coming out? Make a salt paste with cold water. Slap it on. Wait 30 minutes. Scrub gently with a damp cloth.</li>
          <li>Stubborn blood needs hydrogen peroxide. Pour it on. Watch it bubble. That's the proteins breaking down. Wipe with cold water. Done.</li>
        </ol>
        
        <div class="bg-purple-50 p-4 rounded-lg my-6">
          <p class="font-semibold text-purple-800">⚠️ Warning:</p>
          <p class="text-purple-700">Never use hot water on blood stains - it cooks the protein and sets the stain permanently.</p>
        </div>
        
        <h3>Sweat Stains</h3>
        <p>Body oil builds up slowly. Then boom. Yellow mattress.</p>
        <ol class="list-decimal pl-6 space-y-2 my-4">
          <li>Mix hydrogen peroxide, water, and dish soap. Equal amounts. Spray the yellow spots. Peroxide eats the oils. Give it an hour.</li>
          <li>Wipe with a damp cloth. Dump baking soda on it. Leave overnight. Vacuum. White again.</li>
        </ol>
        
        <h3>Spills</h3>
        <ol class="list-decimal pl-6 space-y-2 my-4">
          <li>Move fast. Blot with paper towels immediately.</li>
          <li>Mix dish soap in cool water. Just a bit. Dab with a cloth. Don't soak the mattress. Just lift the stain.</li>
          <li>Coffee or wine? Add vinegar to your soapy water. Cuts through it.</li>
          <li>Rinse by blotting with plain water. Press dry towels on it. Aim a fan at the spot.</li>
        </ol>
        
        <h2>When To Do It Yourself vs Hiring Pros</h2>
        
        <p>Fresh stains? Do it yourself. Saves money. Solves it now.</p>
        
        <p>But sometimes you need help. Huge stains. Multiple old ones. Smell won't go away. That's when you need commercial equipment.</p>
        
        <p>Hotels and Airbnbs should get professional cleaning regularly. Keeps mattresses lasting longer. Keeps guests happy. Regular homes? Maybe once a year.</p>
        
        <p>Pros have steam cleaners. UV sanitizers. Industrial odor killers. They know how to dry it properly without creating mold.</p>
        
        <h2>Don't Make These Mistakes</h2>
        
        <ul class="list-disc pl-6 space-y-2 my-4">
          <li><span class="font-bold">Too Much Water:</span> You'll grow mold. Use spray bottles. Blot. Never pour.</li>
          <li><span class="font-bold">Bleach:</span> Destroys fabric. Smells horrible. Breaks down mattress materials. Just no.</li>
          <li><span class="font-bold">Steam Cleaners:</span> Those carpet ones? Wrong tool. Too much water. Can't extract it. Mold city.</li>
          <li><span class="font-bold">Ignoring Stains:</span> Old stains are ten times harder. Fix it now.</li>
          <li><span class="font-bold">Carpet Cleaners:</span> Bad extraction. Too much water. Doesn't work on mattresses.</li>
        </ul>
        
        <h2>Prevention Tips</h2>
        
        <ul class="list-disc pl-6 space-y-2 my-4">
          <li><span class="font-bold">Buy a Waterproof Protector:</span> Best forty bucks you'll spend. New ones don't make you sweat.</li>
          <li><span class="font-bold">Wash Sheets Weekly:</span> Hot water. Kills dust mites. Removes oils.</li>
          <li><span class="font-bold">Vacuum Monthly:</span> Takes five minutes. Use the brush attachment.</li>
          <li><span class="font-bold">Rotate Every Few Months:</span> Spreads the wear.</li>
          <li><span class="font-bold">Air it Out:</span> Open windows. Strip the bed. Sun and fresh air kill bacteria naturally.</li>
        </ul>
        
        <div class="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-2xl my-8">
          <h3 class="text-2xl font-bold mb-4">Bottom Line</h3>
          <p class="text-lg mb-4">Stains happen. They're fixable.</p>
          <p class="mb-4">Keep vinegar, baking soda, and peroxide handy. When something spills, fix it immediately. Basic supplies beat expensive products.</p>
          <p class="mb-4">Fresh small spills? DIY. Old huge stains or bad smells? Call the pros.</p>
          <p class="text-xl font-bold">You paid good money for that mattress. Take care of it. Better sleep. Healthier bedroom. Prevent what you can. Attack spills fast. Don't let small problems become disasters.</p>
        </div>
      `,
      
      faqs: [
        {
          q: "Can I use bleach on mattress stains?",
          a: "No, never use bleach on mattresses. It damages the fabric, breaks down foam materials, and creates harmful fumes. Stick to vinegar, hydrogen peroxide, and baking soda."
        },
        {
          q: "How long does a mattress take to dry after cleaning?",
          a: "With proper ventilation and fans, a mattress typically takes 2-4 hours to dry completely. In humid conditions, it may take 6-8 hours. Never put sheets back on a damp mattress."
        },
        {
          q: "How often should I deep clean my mattress?",
          a: "For most homes, professional deep cleaning once a year is sufficient. If you have allergies, pets, or children, consider cleaning every 6 months."
        }
      ],
      
      relatedPosts: [
        {
          title: "How Often Should You Clean Your Mattress?",
          slug: "how-often-clean-mattress",
          image: "https://images.unsplash.com/photo-1584102276552-6f4a5c6c1e1d?q=80&w=2070&auto=format&fit=crop",
          excerpt: "Most people never clean their mattress. Here's why that's a problem and what you should do about it.",
          date: "Feb 18, 2025"
        },
        {
          title: "Professional vs DIY Mattress Cleaning",
          slug: "professional-vs-diy-mattress-cleaning",
          image: "https://images.unsplash.com/photo-1582738411706-bfc8e691d1c2?q=80&w=2070&auto=format&fit=crop",
          excerpt: "When should you call the pros and when can you handle it yourself? We break down the costs and benefits.",
          date: "Feb 10, 2025"
        },
        {
          title: "The Truth About Dust Mites in Your Bed",
          slug: "dust-mites-in-bed",
          image: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?q=80&w=2070&auto=format&fit=crop",
          excerpt: "Millions of microscopic creatures live in your mattress. Here's what you need to know about them.",
          date: "Feb 3, 2025"
        }
      ]
    },
    
    "remove-furniture-marks-carpets-steam-cleaning": {
      title: "How to Remove Furniture Marks from Carpets Using Steam Cleaning",
      slug: "remove-furniture-marks-carpets-steam-cleaning",
      excerpt: "That couch sat in the corner for years. Finally moved it last weekend, and guess what? Those nasty indentations are staring right back at me. They won't go away on their own, but carpet steam cleaning can fix them.",
      author: "Michael Chen",
      authorTitle: "Carpet Cleaning Expert",
      authorBio: "Michael has been in the carpet cleaning industry for 8 years, specializing in stain removal and carpet restoration. He's helped hundreds of homeowners restore their carpets.",
      authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop",
      date: "February 20, 2025",
      readTime: "6 min read",
      views: 892,
      likes: 67,
      comments: 15,
      category: "Carpet Cleaning",
      tags: ["carpet cleaning", "steam cleaning", "furniture marks", "DIY tips", "carpet maintenance"],
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
      imageCaption: "Steam cleaning can restore flattened carpet fibers from heavy furniture",
      
      content: `
        <p class="lead">That couch sat in the corner for years. Finally moved it last weekend, and guess what? Those nasty indentations are staring right back at me. They won't go away on their own, but carpet steam cleaning can fix them.</p>
        
        <p>I've removed hundreds of these marks. Steam works better than most other methods. Let me show you how.</p>
        
        <h2>What Causes Furniture Marks in Carpets</h2>
        
        <p>Heavy furniture crushes carpet fibers. The weight pushes them down flat. Over time, they stay that way.</p>
        
        <p>Your carpet has memory. The fibers get used to being compressed. Heavier items like sofas and beds leave deeper marks. The padding underneath gets squashed too.</p>
        
        <p>Plush carpets show dents worse than flat weave styles. But any carpet can develop these marks if furniture sits long enough.</p>
        
        <h2>Why Steam Works on Carpet Fibers</h2>
        
        <p>Here's the thing about modern carpeting, whether it's nylon or polyester, both respond really well when you apply some heat and get them a little damp.</p>
        
        <p>Heat makes the fibers flexible again. Moisture penetrates deep into each strand. Together, they reset the carpet's memory. The fibers can stand back up.</p>
        
        <p>Think of it as coaxing the fibers back to their original shape using warmth and moisture. Here's exactly what to do.</p>
        
        <h2>Step-by-Step Removal Process</h2>
        
        <h3>Vacuum First</h3>
        <p>Run your vacuum over those dents thoroughly. Hit them from multiple directions to get everything out and wake up any fibers that might already be trying to bounce back. This preps the fibers.</p>
        
        <h3>Try Ice Cubes for Stubborn Dents</h3>
        <p>Put ice cubes directly on deep marks. Let them melt overnight. The slow moisture release starts loosening the fibers. Blot up the water before moving on.</p>
        
        <div class="bg-blue-50 p-4 rounded-lg my-6">
          <p class="font-semibold text-blue-800">💡 Pro Tip:</p>
          <p class="text-blue-700">Got a whole house full of marks or high-end carpeting? Bring in the pros through professional carpet cleaning services. Their commercial machines pack way more punch, and they've seen every carpet fiber type imaginable.</p>
        </div>
        
        <h3>Get Your Steam Cleaner Ready</h3>
        <p>Fill it with clean water. Skip the cleaning solution, plain water does the job here.</p>
        
        <p>No steam cleaner at home? Grab a rental from Home Depot or Lowe's for about $30. And forget that DIY iron-and-towel method you've seen on Pinterest, it's more trouble than it's worth.</p>
        
        <h3>Apply the Steam</h3>
        <p>Keep that nozzle about six to eight inches away from the carpet. Give it quick pulses of steam rather than holding it down continuously. The goal is getting it warm and damp, definitely not waterlogged. Work across the entire impression so you treat it evenly.</p>
        
        <h3>Fluff Up the Fibers</h3>
        <p>Now here's what separates okay results from great ones: immediately after steaming, get in there with your hands and physically fluff those fibers upward. Seriously, a regular dinner fork does wonders for this.</p>
        
        <p>Brush in different directions. Help those fibers stand up. Got a carpet rake sitting in your closet? Perfect time to use it.</p>
        
        <h3>Let It Dry Completely</h3>
        <p>After fluffing, just leave everything alone to dry. Open windows. Turn on fans. Stay off that spot for at least a couple hours, maybe longer if your house is humid or the air isn't circulating much. I know it's tempting to check your work, but give it time.</p>
        
        <h3>Repeat If Needed</h3>
        <p>Some really stubborn impressions won't disappear after the first round. Totally expected. Just repeat the process until they're gone.</p>
        
        <h2>DIY vs Hiring Professionals</h2>
        
        <p>You can handle small marks yourself. It's cheap if you already have a steam cleaner.</p>
        
        <p>Professionals make sense for bigger jobs. They work faster across multiple rooms. Their equipment is more powerful.</p>
        
        <p>They also know carpet types. Natural wool carpets need gentler handling compared to synthetic ones. Expensive or fragile materials can actually get worse if you treat them too aggressively.</p>
        
        <p>Check your warranty too. Some require professional cleaning to stay valid.</p>
        
        <h2>Mistakes That Damage Carpets</h2>
        
        <ul class="list-disc pl-6 space-y-2 my-4">
          <li><span class="font-bold">Don't soak the carpet.</span> Too much water causes mold. It ruins the backing and padding underneath.</li>
          <li><span class="font-bold">Don't use excessive heat.</span> Some fibers melt. Always test a hidden spot first.</li>
          <li><span class="font-bold">Don't walk on damp carpet.</span> You'll flatten it again or grind in dirt.</li>
          <li><span class="font-bold">Don't skip the brushing step.</span> Steam alone won't stand the fibers up.</li>
          <li><span class="font-bold">Don't use dirty equipment.</span> You'll spread grime around while trying to fix dents.</li>
        </ul>
        
        <h2>Preventing Future Marks</h2>
        
        <ul class="list-disc pl-6 space-y-2 my-4">
          <li>Put furniture coasters under heavy pieces. They spread out the weight.</li>
          <li>Move furniture a few inches every few months. This prevents permanent compression.</li>
          <li>Choose furniture with wider legs when buying new pieces. Narrow legs create deeper dents.</li>
          <li>Put area rugs under big furniture in visible spots. This protects your main carpet.</li>
        </ul>
        
        <div class="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-2xl my-8">
          <h3 class="text-2xl font-bold mb-4">Final Thoughts</h3>
          <p class="text-lg mb-4">Bottom line? Those furniture impressions can absolutely be fixed. Steam cleaning fixes them when done right.</p>
          <p class="mb-4">The heat softens fibers. The moisture resets them. The brushing helps them stand up. Simple physics.</p>
          <p class="mb-4">Test your technique on an inconspicuous corner before going all-in. Build your confidence. The results genuinely surprised me the first time I tried this method.</p>
          <p class="text-xl font-bold">Start this weekend. Your carpet will look new again.</p>
        </div>
      `,
      
      faqs: [
        {
          q: "Will steam cleaning damage my carpet?",
          a: "No, when done correctly, steam cleaning is safe for most carpets. Just avoid over-wetting and excessive heat. Always test on a hidden area first."
        },
        {
          q: "How long do furniture marks take to disappear?",
          a: "With proper steam treatment, most furniture marks lift immediately. Some deep impressions may need 2-3 treatments and 24-48 hours to fully recover."
        },
        {
          q: "Can I use a clothes iron instead of a steam cleaner?",
          a: "We don't recommend it. Irons can easily overheat and melt carpet fibers. The steam-to-water ratio isn't right either. Stick to proper steam cleaning equipment."
        }
      ],
      
      relatedPosts: [
        {
          title: "Deep Cleaning vs End of Lease Cleaning: Key Differences Explained",
          slug: "deep-cleaning-vs-end-of-lease-cleaning-differences",
          image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
          excerpt: "Pick the wrong cleaning service and you'll regret it. Could cost you money. Could cost you your rental bond.",
          date: "Feb 15, 2025"
        },
        {
          title: "10 Clear Signs It's Time to Hire a Professional Cleaning Service",
          slug: "signs-its-time-to-hire-professional-cleaning",
          image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
          excerpt: "You know that feeling when cleaning takes over your life? Here are ten signs you need to call in the professionals.",
          date: "Feb 10, 2025"
        },
        {
          title: "The Truth About Dust Mites in Your Bed",
          slug: "dust-mites-in-bed",
          image: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?q=80&w=2070&auto=format&fit=crop",
          excerpt: "Millions of microscopic creatures live in your mattress. Here's what you need to know about them.",
          date: "Feb 3, 2025"
        }
      ]
    },
    
    "deep-cleaning-vs-end-of-lease-cleaning-differences": {
      title: "Deep Cleaning vs End of Lease Cleaning: Key Differences Explained",
      slug: "deep-cleaning-vs-end-of-lease-cleaning-differences",
      excerpt: "Pick the wrong cleaning service and you'll regret it. Could cost you money. Could cost you your rental bond. Could waste your entire weekend. Most people think all cleaning is the same. It's not.",
      author: "Sarah Mitchell",
      authorTitle: "Senior Cleaning Specialist",
      authorBio: "With over 10 years of experience in professional cleaning, Sarah has helped hundreds of tenants get their full bond back. She knows exactly what property managers look for.",
      authorImage: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=2070&auto=format&fit=crop",
      date: "February 15, 2025",
      readTime: "7 min read",
      views: 1563,
      likes: 112,
      comments: 31,
      category: "End of Lease",
      tags: ["end of lease cleaning", "bond cleaning", "deep cleaning", "moving tips", "rental advice"],
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
      imageCaption: "Understanding the difference between deep cleaning and end of lease cleaning can save you money and stress",
      
      content: `
        <p class="lead">Pick the wrong cleaning service and you'll regret it. Could cost you money. Could cost you your rental bond. Could waste your entire weekend.</p>
        
        <p>Most people think all cleaning is the same. It's not. Deep cleaning and move-out cleaning are completely different things. Know the difference before you book.</p>
        
        <h2>What Is Deep Cleaning?</h2>
        
        <p>Deep cleaning hits the spots you usually skip. You know, the stuff that builds up over months.</p>
        
        <p>When did you last clean behind your fridge? Pull out the oven to scrub underneath? Wipe down your ceiling fans? Exactly.</p>
        
        <p><span class="font-bold">Kitchen Work:</span> Range hoods get degreased. Ovens get scrubbed inside. Appliances get pulled out and cleaned behind. Cabinet interiors get washed. Walls get wiped down.</p>
        
        <p><span class="font-bold">Bathroom Focus:</span> Soap scum gets removed. Hard water stains disappear. Grout gets scrubbed white again. Exhaust fans get cleaned. Every surface gets sanitized.</p>
        
        <p><span class="font-bold">Living Spaces:</span> Baseboards get washed. Window tracks get cleared out. Furniture gets moved and vacuumed under. Ceiling fans get dusted. Walls and switches get wiped.</p>
        
        <p>Most people book deep cleaning services once or twice a year. It prevents small messes from becoming disasters.</p>
        
        <h2>What Is End of Lease Cleaning?</h2>
        
        <p>Bond cleaning has one job: get your deposit back.</p>
        
        <p>You're moving out. The landlord inspects everything. Miss one spot and you lose money. Sometimes hundreds of dollars. Sometimes thousands.</p>
        
        <p>Move-out cleaning follows your lease agreement. The property must look like it did when you moved in. Normal wear is fine. Dirt isn't.</p>
        
        <p><span class="font-bold">What's Included:</span> Everything on the inspection checklist. Walls, ceilings, floors, windows, blinds, lights, switches, doors, cabinets, appliances, carpets, bathrooms, outdoor areas. All of it.</p>
        
        <p><span class="font-bold">Why It Matters:</span> Property managers use checklists. They check inside ovens. Behind fridges. Carpet corners. Inside cupboards. One missed area fails you.</p>
        
        <p>Professional end of lease cleaning follows property management standards. Cleaners know what inspectors look for.</p>
        
        <div class="bg-blue-50 p-6 rounded-xl my-8">
          <h3 class="text-xl font-bold mb-4">Key Differences Between Them</h3>
          <ul class="space-y-3">
            <li><span class="font-bold">Purpose:</span> Deep cleaning improves your living space. Bond cleaning protects your deposit.</li>
            <li><span class="font-bold">Coverage:</span> Deep cleaning targets problem areas in occupied homes. Move-out cleaning covers everything, even spots you don't care about.</li>
            <li><span class="font-bold">Standards:</span> Deep cleaning meets general cleanliness levels. Bond cleaning meets specific lease requirements.</li>
            <li><span class="font-bold">Timing:</span> Deep cleaning happens while you live there. End of lease cleaning happens once when you leave.</li>
            <li><span class="font-bold">Guarantees:</span> Move-out services often guarantee bond returns. Deep cleaning doesn't need guarantees because there's no inspection.</li>
            <li><span class="font-bold">Carpets:</span> Deep cleaning might include carpet shampooing. Bond cleaning always requires professional steam cleaning with receipts.</li>
          </ul>
        </div>
        
        <h2>When to Choose Each Option</h2>
        
        <h3>Pick Deep Cleaning When:</h3>
        <ul class="list-disc pl-6 space-y-2 my-4">
          <li>You're staying put and want a refresh.</li>
          <li>Regular cleaning isn't cutting it anymore.</li>
          <li>You're hosting people soon.</li>
          <li>Allergies are acting up.</li>
          <li>Problem areas need attention.</li>
        </ul>
        
        <h3>Pick End of Lease Cleaning When:</h3>
        <ul class="list-disc pl-6 space-y-2 my-4">
          <li>You're moving out of a rental.</li>
          <li>Your lease requires professional cleaning.</li>
          <li>You want your full bond back.</li>
          <li>Moving has you too busy to clean.</li>
          <li>Property managers want receipts.</li>
        </ul>
        
        <h2>Cost, Time, and Scope Comparison</h2>
        
        <div class="overflow-x-auto my-6">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-gray-100">
                <th class="p-3 text-left">Aspect</th>
                <th class="p-3 text-left">Deep Cleaning</th>
                <th class="p-3 text-left">Bond Cleaning</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b">
                <td class="p-3 font-bold">Prices</td>
                <td class="p-3">$200-$500 for 3-bedroom house</td>
                <td class="p-3">$250-$600+ for same house</td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-bold">Carpets</td>
                <td class="p-3">Optional extra</td>
                <td class="p-3">Included, $100-$200 extra</td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-bold">Time</td>
                <td class="p-3">4-8 hours</td>
                <td class="p-3">6-10 hours</td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-bold">Flexibility</td>
                <td class="p-3">Customizable</td>
                <td class="p-3">Everything done, no exceptions</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <h2>Common Mistakes People Make</h2>
        
        <ul class="list-disc pl-6 space-y-2 my-4">
          <li><span class="font-bold">Thinking Regular Cleaning Works for Move-Outs:</span> Big mistake. Regular cleaning misses what property managers check. Tenants lose bonds thinking "clean enough" works. It doesn't.</li>
          <li><span class="font-bold">Booking Deep Cleaning for Lease End:</span> Deep cleaners might not follow property checklists. They miss items that fail inspections. Always specify bond cleaning.</li>
          <li><span class="font-bold">Going DIY for Move-Outs:</span> Rarely works. You don't know inspection standards. You lack commercial equipment. Failed inspection means hiring pros anyway or losing money.</li>
          <li><span class="font-bold">Ignoring Lease Requirements:</span> Your lease lists specific tenant responsibilities. Some require carpet cleaning receipts. Some want windows done. Some demand oven cleaning. Read it first.</li>
          <li><span class="font-bold">Waiting Too Long:</span> Good cleaners book up fast during moving season. Waiting until three days before lease end limits options. Might force expensive or poor choices.</li>
        </ul>
        
        <div class="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-2xl my-8">
          <h3 class="text-2xl font-bold mb-4">Final Thoughts</h3>
          <p class="text-lg mb-4">Your situation determines the right choice. Living there and want maintenance? Deep cleaning works. Moving out and need your bond? End of lease cleaning is required.</p>
          <p class="mb-4">Don't risk your deposit by picking wrong or doing it yourself. Professional cleaners know property inspection requirements. They have proper equipment. Many guarantee results.</p>
          <p class="text-xl font-bold">Book early. Choose pros experienced in what you need. Your home and wallet will benefit.</p>
        </div>
      `,
      
      faqs: [
        {
          q: "Will any cleaning service guarantee my bond back?",
          a: "Not all services offer bond guarantees. Look for cleaners specifically specializing in end of lease cleaning who offer a written bond back guarantee."
        },
        {
          q: "How far in advance should I book end of lease cleaning?",
          a: "Book at least 1-2 weeks in advance, especially during peak moving seasons (end/start of month, school holidays). Last-minute bookings may be limited or more expensive."
        },
        {
          q: "What happens if the property manager rejects the clean?",
          a: "Reputable end of lease cleaning services will return to fix any issues at no additional cost. Always get this guarantee in writing before booking."
        }
      ],
      
      relatedPosts: [
        {
          title: "10 Clear Signs It's Time to Hire a Professional Cleaning Service",
          slug: "signs-its-time-to-hire-professional-cleaning",
          image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
          excerpt: "You know that feeling when cleaning takes over your life? Here are ten signs you need to call in the professionals.",
          date: "Feb 10, 2025"
        },
        {
          title: "How to Remove Furniture Marks from Carpets Using Steam Cleaning",
          slug: "remove-furniture-marks-carpets-steam-cleaning",
          image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
          excerpt: "That couch sat in the corner for years. Finally moved it last weekend, and those nasty indentations are staring right back at me.",
          date: "Feb 20, 2025"
        },
        {
          title: "The Truth About Dust Mites in Your Bed",
          slug: "dust-mites-in-bed",
          image: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?q=80&w=2070&auto=format&fit=crop",
          excerpt: "Millions of microscopic creatures live in your mattress. Here's what you need to know about them.",
          date: "Feb 3, 2025"
        }
      ]
    },
    
    "signs-its-time-to-hire-professional-cleaning": {
      title: "10 Clear Signs It's Time to Hire a Professional Cleaning Service",
      slug: "signs-its-time-to-hire-professional-cleaning",
      excerpt: "You know that feeling when cleaning takes over your life? Saturday gets eaten up scrubbing bathrooms and mopping. Sunday? Same story. The dust situation alone is driving you crazy. Here are ten signs you need to call in the professionals.",
      author: "Emma Watson",
      authorTitle: "Cleaning Specialist",
      authorBio: "Emma has been helping Melbourne families maintain cleaner homes for 6 years. She's passionate about making cleaning manageable through smart strategies and professional help.",
      authorImage: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=2070&auto=format&fit=crop",
      date: "February 10, 2025",
      readTime: "9 min read",
      views: 2145,
      likes: 178,
      comments: 42,
      category: "Cleaning Tips",
      tags: ["professional cleaning", "cleaning services", "home maintenance", "time management", "cleaning tips"],
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
      imageCaption: "Sometimes the smartest move is knowing when to call the pros",
      
      content: `
        <p class="lead">You know that feeling when cleaning takes over your life? Saturday gets eaten up scrubbing bathrooms and mopping. Sunday? Same story, dusting, organizing, wiping everything down. Three days later your place looks like you did nothing.</p>

        <p>The dust situation alone is driving you crazy. Can't stop sneezing. Eyes won't quit being red and watery. Every flat surface in your house has turned into a dumping ground. Your kitchen counters? Let's just say bacteria love it there. At some point you have to ask yourself: is this really how you want to spend your life?</p>

        <p>Ten signs you need to call in the professionals:</p>

        <h2>1. You're Always Behind on Cleaning</h2>
        <p>Scrubbed the bathroom Saturday morning? Tuesday rolls around and it's disgusting again. That laundry basket you just emptied? Full again by tomorrow. Finish one cleaning job and somehow four more are waiting.</p>
        <p>This cycle eats up all your time. Hire a professional cleaning service and it stops. Finally get your weekends back for things you actually want to do.</p>

        <h2>2. Allergies Won't Go Away</h2>
        <p>Can't stop sneezing at home? Eyes itching and watering every single morning? Something's definitely off.</p>
        <p>Your mattress and pillows are probably crawling with dust mites right now. Pet hair has worked itself so deep into your carpets that no vacuum you own can get it out. Mold's growing in bathroom corners you can't even see yet. Running the vacuum every week doesn't come close to fixing these problems.</p>
        <p>Get pros in for deep cleaning. Their HEPA-filter equipment actually traps particles instead of just moving them around. Anyone with asthma or allergies feels better fast.</p>

        <h2>3. Guests Are Coming Over</h2>
        <p>Got a dinner party coming up? Family dropping by soon? Immediate panic mode.</p>
        <p>Making your place look decent takes forever. Why kill yourself doing it? Professionals knock this out efficiently. They hit baseboards, ceiling fans, all those spots you normally ignore.</p>
        <p>You concentrate on food and hosting. They ensure your space impresses guests.</p>

        <h2>4. Work Got Busier</h2>
        <p>New job? Big promotion? Your schedule just exploded. Something has to give.</p>
        <p>Usually, it's cleaning. You come home tired. Scrubbing floors sounds terrible. This is when residential cleaning services make the most sense.</p>
        <p>Those evening hours become yours again. Relax without guilt. Stop trying to handle everything yourself and life becomes manageable again.</p>

        <h2>5. You Just Moved</h2>
        <p>Moving into a new place? Previous tenants leave surprises. Sticky shelves. Dirty fans. Mystery stains everywhere.</p>
        <p>You're already unpacking boxes and dealing with utilities. Adding deep cleaning to that list is too much. Professional move-in cleaning handles everything first.</p>
        <p>Bathrooms get sanitized. Kitchens get scrubbed top to bottom. Every cabinet and appliance gets cleaned inside and out.</p>
        <p>Walking into a genuinely clean space on day one? That's how you want to start.</p>

        <h2>6. Your Office Looks Bad</h2>
        <p>Dirty breakrooms kill morale. Grimy bathrooms make employees uncomfortable. Cluttered desks create chaos.</p>
        <p>Workspace hygiene matters more than people admit. Employees notice when things get gross. Clients definitely notice. Regular commercial cleaning services keep everything under control.</p>
        <p>Clean offices also mean fewer people calling in sick. When someone regularly sanitizes doorknobs, keyboards, and shared surfaces, germs stop spreading through your entire team.</p>

        <h2>7. Physical Limitations Make It Hard</h2>
        <p>Bad back? Joint problems? Recovering from surgery? Certain cleaning tasks become dangerous.</p>
        <p>Bending to scrub tubs risks injury. Reaching up to dust ceiling fans could throw out your back. You probably shouldn't be hauling furniture around to vacuum underneath.</p>
        <p>Maid services exist for exactly this reason. Your home stays clean without risking your health.</p>

        <h2>8. You're Selling or Renting Property</h2>
        <p>Real estate moves fast. Buyers walk through the door and form an opinion in under thirty seconds.</p>
        <p>A spotless property photographs better and shows stronger. Hire professionals to deep clean before listing. They strip away accumulated grime from years of living there. Your property's true condition becomes visible. Spotless windows bring in significantly more natural light. Gleaming bathrooms suddenly feel twice as large. That spotless kitchen tells buyers this property has been maintained properly.</p>
        <p>The investment often returns through faster sales or higher rent.</p>

        <h2>9. Big Tasks Keep Getting Postponed</h2>
        <p>Your oven hasn't seen a proper cleaning in what, two years? Three? Window tracks are caked with black grime. That white grout turned gray so gradually you stopped noticing.</p>
        <p>These jobs stick around. They just get worse. Call in professionals and watch what happens. They arrive with industrial-grade equipment and commercial products that actually work. Surfaces you'd written off as permanently ruined? They bring them back.</p>

        <h2>10. You Hate Cleaning</h2>
        <p>Not everyone enjoys this stuff. If you'd rather get a root canal than scrub toilets, why keep forcing yourself?</p>
        <p>Calculate what your time is worth. Count up weekly cleaning hours. Check what professionals charge. The numbers usually make the decision obvious.</p>
        <p>Money aside, though, removing that one task you absolutely dread transforms how you feel about your entire week.</p>

        <div class="bg-blue-50 p-6 rounded-xl my-8">
          <h3 class="text-xl font-bold mb-4">Making the Decision</h3>
          <p class="mb-4">Spotting these signs is the easy part. Acting on them without feeling guilty? That's harder. But getting help isn't lazy. It's smart resource management.</p>
          <p class="mb-4">Start by identifying your needs. Homes benefit from regular scheduled visits. Offices need after-hours cleaning to avoid disrupting workflow. One-time deep cleaning works for special events or moves.</p>
          <p class="mb-4">Find companies with solid reviews and proper insurance. Ask about their methods and what products they use. Verify they can accommodate your specific requirements.</p>
          <p class="mb-4">Your environment shapes everything. Lower stress. Better health. More hours available for activities you actually care about.</p>
          <p class="text-xl font-bold">These signs matching your life right now? Consider giving professional help a shot. No rule says you have to do this solo.</p>
        </div>
      `,
      
      faqs: [
        {
          q: "How much does professional cleaning cost in Melbourne?",
          a: "Prices vary based on home size and service type. Standard cleaning ranges from $129-$249, deep cleaning $349-$549, and end of lease $299-$499. Most companies offer free quotes."
        },
        {
          q: "How often should I schedule professional cleaning?",
          a: "For maintenance, every 2-4 weeks works well. Deep cleaning twice a year keeps everything fresh. End of lease is a one-time service when moving out."
        },
        {
          q: "Are cleaning products safe for children and pets?",
          a: "Reputable companies use eco-friendly, non-toxic products safe for families. Always ask about their cleaning solutions if you have specific concerns."
        }
      ],
      
      relatedPosts: [
        {
          title: "Deep Cleaning vs End of Lease Cleaning: Key Differences Explained",
          slug: "deep-cleaning-vs-end-of-lease-cleaning-differences",
          image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
          excerpt: "Pick the wrong cleaning service and you'll regret it. Could cost you money. Could cost you your rental bond.",
          date: "Feb 15, 2025"
        },
        {
          title: "How to Remove Furniture Marks from Carpets Using Steam Cleaning",
          slug: "remove-furniture-marks-carpets-steam-cleaning",
          image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
          excerpt: "That couch sat in the corner for years. Finally moved it last weekend, and those nasty indentations are staring right back at me.",
          date: "Feb 20, 2025"
        },
        {
          title: "The Truth About Dust Mites in Your Bed",
          slug: "dust-mites-in-bed",
          image: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?q=80&w=2070&auto=format&fit=crop",
          excerpt: "Millions of microscopic creatures live in your mattress. Here's what you need to know about them.",
          date: "Feb 3, 2025"
        }
      ]
    }
  };

  const post = postsData[slug];

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={40} className="text-red-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-8 text-lg">
            The article you're looking for doesn't exist or has been moved.
          </p>
          <button
            onClick={() => navigate('/blogs')}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold transition-all hover:scale-[1.02] shadow-lg"
          >
            <ArrowLeft size={20} />
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  // // Set initial like count
  // React.useEffect(() => {
  //   setLikeCount(post.likes);
  // }, [post.likes]);

  // Handle comment submit
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setCommentStatus('success');
      setCommentData({ name: '', email: '', comment: '' });
      setIsSubmitting(false);
      setTimeout(() => setCommentStatus(null), 5000);
    }, 1500);
  };

  // Handle like
  const handleLike = () => {
    if (liked) {
      setLikeCount(prev => prev - 1);
    } else {
      setLikeCount(prev => prev + 1);
    }
    setLiked(!liked);
  };

  // Blog Post Schema
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": "2025-02-24T08:00:00+11:00",
    "dateModified": "2025-02-24T08:00:00+11:00",
    "author": {
      "@type": "Person",
      "name": post.author,
      "description": post.authorBio,
      "image": post.authorImage,
      "jobTitle": post.authorTitle,
      "worksFor": {
        "@type": "CleaningService",
        "name": "Knight Cleanings",
        "url": "https://knightcleanings.com.au"
      }
    },
    "publisher": {
      "@type": "CleaningService",
      "name": "Knight Cleanings",
      "logo": {
        "@type": "ImageObject",
        "url": "https://knightcleanings.com.au/logo.png"
      },
      "url": "https://knightcleanings.com.au",
      "telephone": "+61420499661",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "11 Warath Drive, Dandenong North",
        "addressLocality": "Melbourne",
        "addressRegion": "VIC",
        "postalCode": "3175",
        "addressCountry": "AU"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://knightcleanings.com.au/blog/${post.slug}`
    },
    "keywords": post.tags.join(", "),
    "articleSection": post.category,
    "wordCount": 1250,
    "timeRequired": post.readTime,
    "inLanguage": "en-AU"
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faqs?.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Knight Cleanings Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.tags.join(", ")} />
        <meta name="author" content={post.author} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={`https://knightcleanings.com.au/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Knight Cleanings" />
        <meta property="article:published_time" content="2025-02-24T08:00:00+11:00" />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map(tag => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image} />
        <meta name="twitter:creator" content="@knightcleanings" />
        <link rel="canonical" href={`https://knightcleanings.com.au/blog/${post.slug}`} />
        
        {/* JSON-LD Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
        
        {post.faqs && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://knightcleanings.com.au"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://knightcleanings.com.au/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": post.title,
                "item": `https://knightcleanings.com.au/blog/${post.slug}`
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Blog Post Content */}
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative h-[70vh] min-h-[600px]">
          <div className="absolute inset-0">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
          </div>
          
          <div className="absolute bottom-0 left-0 right-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 text-white">
              <button
                onClick={() => navigate('/blog')}
                className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors group"
              >
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Back to Blog
              </button>
              
              <div className="flex flex-wrap items-center gap-3 text-sm mb-4">
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-1.5 rounded-full font-medium">
                  {post.category}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={16} />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={16} />
                  {post.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <Eye size={16} />
                  {post.views.toLocaleString()} views
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 max-w-4xl leading-tight">
                {post.title}
              </h1>
              
              <div className="flex items-center gap-4">
                <img 
                  src={post.authorImage} 
                  alt={post.author}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <p className="font-semibold text-lg">{post.author}</p>
                  <p className="text-sm text-gray-200">{post.authorTitle}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-8">
              {/* Main Article */}
              <article className="lg:col-span-8">
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                  {/* Author Bio */}
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl mb-8">
                    <img 
                      src={post.authorImage} 
                      alt={post.author}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white"
                    />
                    <div>
                      <p className="font-bold text-gray-900 text-lg">{post.author}</p>
                      <p className="text-sm text-gray-600">{post.authorTitle}</p>
                      <p className="text-sm text-gray-600 mt-1">{post.authorBio}</p>
                    </div>
                  </div>

                  {/* Post Stats */}
                  <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-gray-200">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Eye size={20} />
                      <span>{post.views.toLocaleString()} views</span>
                    </div>
                    <button 
                      onClick={handleLike}
                      className={`flex items-center gap-2 transition-colors ${
                        liked ? 'text-red-500' : 'text-gray-600 hover:text-red-500'
                      }`}
                    >
                      <Heart size={20} fill={liked ? 'currentColor' : 'none'} />
                      <span>{likeCount} likes</span>
                    </button>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MessageSquare size={20} />
                      <span>{post.comments} comments</span>
                    </div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setLiked(!liked)}
                        className={`p-2 rounded-full transition-colors ${
                          liked ? 'bg-red-50 text-red-500' : 'hover:bg-gray-100 text-gray-600'
                        }`}
                      >
                        <Heart size={20} fill={liked ? 'currentColor' : 'none'} />
                      </button>
                      <button 
                        onClick={() => setSaved(!saved)}
                        className={`p-2 rounded-full transition-colors ${
                          saved ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100 text-gray-600'
                        }`}
                      >
                        <Bookmark size={20} fill={saved ? 'currentColor' : 'none'} />
                      </button>
                      <button 
                        onClick={() => setShowShareModal(true)}
                        className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                      >
                        <Share2 size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Image Caption */}
                  {post.imageCaption && (
                    <p className="text-sm text-gray-500 italic mb-6 text-center">
                      {post.imageCaption}
                    </p>
                  )}

                  {/* Post Content */}
                  <div 
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />

                  {/* Tags */}
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-bold mb-4">Tags:</h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map(tag => (
                        <span 
                          key={tag}
                          onClick={() => navigate(`/blog?tag=${tag}`)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-pointer font-medium"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Share Section */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                    <h3 className="text-lg font-bold mb-4">Share this article:</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <button 
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://knightcleanings.com.au/blog/${post.slug}`)}`, '_blank')}
                        className="bg-[#4267B2] hover:bg-[#365899] text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <Facebook size={18} />
                        <span className="hidden sm:inline">Facebook</span>
                      </button>
                      <button 
                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://knightcleanings.com.au/blog/${post.slug}`)}`, '_blank')}
                        className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <Twitter size={18} />
                        <span className="hidden sm:inline">Twitter</span>
                      </button>
                      <button 
                        onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://knightcleanings.com.au/blog/${post.slug}`)}&title=${encodeURIComponent(post.title)}`, '_blank')}
                        className="bg-[#0077B5] hover:bg-[#006699] text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <Linkedin size={18} />
                        <span className="hidden sm:inline">LinkedIn</span>
                      </button>
                      <button 
                        onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${post.title} - https://knightcleanings.com.au/blog/${post.slug}`)}`, '_blank')}
                        className="bg-[#25D366] hover:bg-[#20b859] text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <MessageCircle size={18} />
                        <span className="hidden sm:inline">WhatsApp</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Comments Section */}
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mt-8">
                  <h2 className="text-2xl font-bold mb-6">Comments ({post.comments})</h2>
                  
                  {/* Comment Form */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold mb-4">Leave a Comment</h3>
                    
                    {commentStatus === 'success' && (
                      <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center gap-3">
                        <CheckCircle size={20} className="flex-shrink-0" />
                        <span>Thank you! Your comment has been submitted and will appear after approval.</span>
                      </div>
                    )}

                    {commentStatus === 'error' && (
                      <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-3">
                        <AlertCircle size={20} className="flex-shrink-0" />
                        <span>Sorry, something went wrong. Please try again.</span>
                      </div>
                    )}

                    <form onSubmit={handleCommentSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Your Name *
                          </label>
                          <input 
                            type="text" 
                            value={commentData.name}
                            onChange={(e) => setCommentData({...commentData, name: e.target.value})}
                            placeholder="John Smith"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Your Email * (will not be published)
                          </label>
                          <input 
                            type="email" 
                            value={commentData.email}
                            onChange={(e) => setCommentData({...commentData, email: e.target.value})}
                            placeholder="john@example.com"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Comment *
                        </label>
                        <textarea 
                          rows="4"
                          value={commentData.comment}
                          onChange={(e) => setCommentData({...commentData, comment: e.target.value})}
                          placeholder="Share your thoughts..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        ></textarea>
                      </div>
                      <button 
                        type="submit"
                        disabled={isSubmitting}
                        className={`bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-8 py-3 rounded-lg transition-all ${
                          isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:scale-[1.02]'
                        }`}
                      >
                        {isSubmitting ? 'Posting...' : 'Post Comment'}
                      </button>
                    </form>
                  </div>

                  {/* Comments List */}
                  <div className="space-y-6">
                    {[
                      {
                        name: "John Smith",
                        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop",
                        date: "2 hours ago",
                        comment: "Great article! The hydrogen peroxide trick really works. I tried it on an old blood stain and it disappeared completely.",
                        likes: 12
                      },
                      {
                        name: "Emily Brown",
                        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=2070&auto=format&fit=crop",
                        date: "1 day ago",
                        comment: "Wish I had read this before ruining my mattress with too much water. Learned the hard way. Will definitely call professionals for the next big stain.",
                        likes: 8
                      }
                    ].map((comment, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                        <img 
                          src={comment.avatar} 
                          alt={comment.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-bold text-gray-900">{comment.name}</span>
                              <span className="text-sm text-gray-500 ml-2">{comment.date}</span>
                            </div>
                            <button className="text-gray-500 hover:text-blue-600 transition-colors flex items-center gap-1">
                              <ThumbsUp size={16} />
                              <span>{comment.likes}</span>
                            </button>
                          </div>
                          <p className="text-gray-700">{comment.comment}</p>
                          <button className="text-sm text-blue-600 hover:text-blue-800 mt-2 transition-colors font-medium">
                            Reply
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Load More Comments */}
                  <div className="text-center mt-8">
                    <button className="text-blue-600 hover:text-blue-800 font-semibold transition-colors">
                      Load More Comments
                    </button>
                  </div>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:col-span-4 space-y-8">
                {/* Author Card */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <User size={20} className="text-blue-600" />
                    About the Author
                  </h3>
                  <div className="text-center">
                    <img 
                      src={post.authorImage} 
                      alt={post.author}
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-blue-100"
                    />
                    <h4 className="font-bold text-lg">{post.author}</h4>
                    <p className="text-sm text-gray-600 mb-2">{post.authorTitle}</p>
                    <p className="text-gray-700 text-sm mb-4">{post.authorBio}</p>
                    <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-lg transition-all hover:scale-[1.02]">
                      Follow Author
                    </button>
                  </div>
                </div>

                {/* Related Posts */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {post.relatedPosts.map(related => (
                      <div 
                        key={related.slug}
                        onClick={() => navigate(`/blog/${related.slug}`)}
                        className="flex gap-3 cursor-pointer group"
                      >
                        <img 
                          src={related.image} 
                          alt={related.title}
                          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                            {related.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">{related.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <button 
                      onClick={() => navigate('/blog')}
                      className="text-blue-600 hover:text-blue-800 font-semibold text-sm transition-colors"
                    >
                      View All Articles →
                    </button>
                  </div>
                </div>

                {/* Services CTA */}
                <div className="bg-gradient-to-r from-blue-700 to-indigo-700 rounded-2xl shadow-xl p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Need Professional Help?</h3>
                  <p className="text-blue-100 mb-4 text-sm">
                    For tough stains or deep cleaning, our experts are ready to help.
                  </p>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold py-3 rounded-lg transition-all hover:scale-[1.02] mb-3"
                  >
                    Book Professional Cleaning
                  </button>
                  <button 
                    onClick={() => window.open('https://wa.me/61420499661', '_blank')}
                    className="w-full border-2 border-white/30 hover:border-white hover:bg-white/10 backdrop-blur-sm font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={18} />
                    WhatsApp Us Now
                  </button>
                </div>

                {/* Categories */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Categories</h3>
                  <div className="space-y-2">
                    {[
                      "Mattress Cleaning",
                      "Carpet Cleaning",
                      "Upholstery Cleaning",
                      "End of Lease",
                      "Cleaning Tips",
                      "DIY Guides"
                    ].map(category => (
                      <div 
                        key={category}
                        onClick={() => navigate(`/blog?category=${category.toLowerCase().replace(' ', '-')}`)}
                        className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 cursor-pointer hover:text-blue-600 transition-colors group"
                      >
                        <span>{category}</span>
                        <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Popular Tags */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Popular Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {["mattress cleaning", "stain removal", "carpet care", "DIY", "professional cleaning", "end of lease", "bond back", "cleaning tips"].map(tag => (
                      <span 
                        key={tag}
                        onClick={() => navigate(`/blog?tag=${tag}`)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-pointer"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Get in Touch</h3>
                  <div className="space-y-4">
                    <a href="tel:+61420499661" className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors group">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <Phone size={18} className="text-blue-600" />
                      </div>
                      <span className="font-medium">+61 420 499 661</span>
                    </a>
                    <a href="mailto:info@knightcleanings.com.au" className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors group">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <Mail size={18} className="text-blue-600" />
                      </div>
                      <span className="font-medium">info@knightcleanings.com.au</span>
                    </a>
                    <div className="flex items-center gap-3 text-gray-700 group">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <MapPin size={18} className="text-blue-600" />
                      </div>
                      <span className="font-medium">Dandenong North, Melbourne VIC</span>
                    </div>
                  </div>
                </div>

                {/* Newsletter */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-xl p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Get Weekly Tips</h3>
                  <p className="text-purple-100 text-sm mb-4">
                    Subscribe for cleaning guides, DIY hacks, and exclusive offers.
                  </p>
                  <form className="space-y-3">
                    <input 
                      type="email" 
                      placeholder="Your email"
                      className="w-full px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                    <button className="w-full bg-white text-purple-700 hover:bg-purple-50 font-bold py-3 rounded-lg transition-all hover:scale-[1.02]">
                      Subscribe
                    </button>
                  </form>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        {post.faqs && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {post.faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl shadow-md overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900">{faq.q}</span>
                      {expandedFAQ === index ? (
                        <ChevronRight size={20} className="transform rotate-90 transition-transform" />
                      ) : (
                        <ChevronRight size={20} className="transition-transform" />
                      )}
                    </button>
                    {expandedFAQ === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Newsletter CTA */}
        <section className="py-16 bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get More Cleaning Tips
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Subscribe to our newsletter for weekly cleaning guides, DIY tips, and special offers.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input 
                type="email" 
                placeholder="Your email address"
                className="flex-1 px-6 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-blue-300"
              />
              <button className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 whitespace-nowrap">
                Subscribe
              </button>
            </form>
            <p className="text-blue-200 mt-4 text-sm">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </section>

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Share this Article</h3>
                <button 
                  onClick={() => setShowShareModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-3">
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(`https://knightcleanings.com.au/blog/${post.slug}`);
                    alert('Link copied to clipboard!');
                  }}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Share2 size={18} className="text-blue-600" />
                  </div>
                  <span className="font-medium">Copy Link</span>
                </button>
                <button 
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://knightcleanings.com.au/blog/${post.slug}`)}`, '_blank')}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-[#4267B2] bg-opacity-10 flex items-center justify-center">
                    <Facebook size={18} className="text-[#4267B2]" />
                  </div>
                  <span className="font-medium">Facebook</span>
                </button>
                <button 
                  onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://knightcleanings.com.au/blog/${post.slug}`)}`, '_blank')}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-[#1DA1F2] bg-opacity-10 flex items-center justify-center">
                    <Twitter size={18} className="text-[#1DA1F2]" />
                  </div>
                  <span className="font-medium">Twitter</span>
                </button>
                <button 
                  onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://knightcleanings.com.au/blog/${post.slug}`)}&title=${encodeURIComponent(post.title)}`, '_blank')}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-[#0077B5] bg-opacity-10 flex items-center justify-center">
                    <Linkedin size={18} className="text-[#0077B5]" />
                  </div>
                  <span className="font-medium">LinkedIn</span>
                </button>
                <button 
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${post.title} - https://knightcleanings.com.au/blog/${post.slug}`)}`, '_blank')}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-[#25D366] bg-opacity-10 flex items-center justify-center">
                    <MessageCircle size={18} className="text-[#25D366]" />
                  </div>
                  <span className="font-medium">WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp Floating Button */}
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => window.open(`https://wa.me/61420499661?text=${encodeURIComponent(`Hi! I just read your article "${post.title}" and have a question.`)}`, '_blank')}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 animate-bounce flex items-center justify-center"
          >
            <MessageCircle size={28} />
          </button>
        </div>

        {/* Back to Top Button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 left-6 z-40 bg-white text-blue-600 p-3 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 border border-gray-200"
        >
          <ArrowLeft size={24} className="transform rotate-90" />
        </button>
      </div>
    </>
  );
};

export default BlogPost;